import React, { useState } from "react";
import { useCreateNewsFlashMutation, useGetAllNewsFlashesQuery, useUpdateNewsFlashMutation, useDeleteNewsFlashMutation } from "@/features/api/newsFlashApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FaEdit, FaTrashAlt, FaSave, FaRegClock } from "react-icons/fa"; // Import icons
import { Loader2 } from "lucide-react"; // Add this import

const NewsFlashAdmin = () => {
  const [message, setMessage] = useState("");
  const [editId, setEditId] = useState(null); // Track the ID of the news flash being edited
  const { data: flashes, refetch } = useGetAllNewsFlashesQuery();
  const [createNewsFlash, { isLoading: creating }] = useCreateNewsFlashMutation();
  const [updateNewsFlash, { isLoading: updating }] = useUpdateNewsFlashMutation();
  const [deleteNewsFlash, { isLoading: isDeleting }] = useDeleteNewsFlashMutation();

  // Handle creating a new news flash
  const handleSubmit = async () => {
    try {
      if (editId) {
        // Update the existing news flash
        await updateNewsFlash({ id: editId, data: { message, isActive: true } }).unwrap();
        toast.success("News flash updated");
        setEditId(null); // Clear edit ID
      } else {
        // Create a new news flash
        await createNewsFlash({ message, isActive: true }).unwrap();
        toast.success("News flash created");
      }
      setMessage(""); // Clear the input field
      refetch(); // Refetch the list of news flashes
    } catch (err) {
      toast.error(editId ? "Failed to update news flash" : "Failed to create news flash");
    }
  };

  // Handle deleting a news flash
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this newsflash?")) return;
  
    try {
      console.log(`Sending DELETE request to: /news-flash/${id}`);
      await deleteNewsFlash(id).unwrap();
      toast.success("Newsflash deleted successfully!");
      refetch(); // Refetch to update the list after delete
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error(err?.data?.message || err?.message || "Failed to delete news flash");
    }
  };
  

  // Set the news flash as active (published)
  const handleSetActive = async (id) => {
    try {
      await updateNewsFlash({ id, data: { isActive: true } }).unwrap();
      toast.success("Set as Published");
      refetch();
    } catch (err) {
      toast.error("Failed to set as published");
    }
  };

  // Set the news flash as draft (inactive)
  const handleDraft = async (id) => {
    try {
      await updateNewsFlash({ id, data: { isActive: false } }).unwrap();
      toast.success("Set as Draft");
      refetch();
    } catch (err) {
      toast.error("Failed to set as draft");
    }
  };

  // Handle edit button click: pre-fill the form with the current message
  const handleEdit = (item) => {
    setMessage(item.message);
    setEditId(item._id); // Set the item ID for editing
  };

  return (
   <div className="flex-1 p-6 bg-background min-h-screen">
  <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
    <h1 className="text-2xl font-semibold">📰 Manage News Flash</h1>
    </div>

      <div className="flex gap-2 mb-4">
        <Input
          placeholder="Enter news message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <Button onClick={handleSubmit} disabled={creating || updating}>
          {creating || updating ? "Saving..." : editId ? "Update" : "Add"}
        </Button>
      </div>

      <div className="space-y-2">
        {flashes?.map((item) => (
          <div key={item._id} className="border p-2 rounded flex justify-between items-center">
            <p>{item.message}</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => handleEdit(item)}>
                <FaEdit />
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleDelete(item._id)} // Corrected from page._id to item._id
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <FaTrashAlt className="h-4 w-4 mr-1" />
                    Delete
                  </>
                )}
              </Button>

              {item.isActive ? (
                <Button size="sm" onClick={() => handleDraft(item._id)}>
                  <FaRegClock /> Draft
                </Button>
              ) : (
                <Button size="sm" onClick={() => handleSetActive(item._id)}>
                  <FaSave /> Publish
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NewsFlashAdmin;
